import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  UserGetOtcPriceParams,
  UserOTCPlaceOrderInput,
  UserOTCPayOrderInput,
  UserOTCPlaceConvertOrderInput,
  UserOTCFinalizeConvertOrderInput,
  UserOtcGetHistoryParams,
} from '../api'
import type { IMutationArgs } from '../types'
import {
  getOtcHistoryInfiniteOptions,
  getOtcPriceOptions,
} from './user-otc.options'
import { EUserOtcQueryKey } from './user-otc.types'
import userOtcService from './user-otc.service'
import { EUserPortfolioQueryKey } from '../user-portfolio'

export const useOtcPayOrder = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (data: UserOTCPayOrderInput) =>
      userOtcService.otcPayOrder(data),
    mutationKey: [EUserOtcQueryKey.OTC_PAY_ORDER],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EUserOtcQueryKey.GET_OTC_PRICE],
        })
      }
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}

export const useOtcPlaceOrder = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserOTCPlaceOrderInput) =>
      userOtcService.otcPlaceOrder(data),
    mutationKey: [EUserOtcQueryKey.OTC_PLACE_ORDER],
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserOtcQueryKey.GET_OTC_PRICE],
        })

      return data
    },
  })
}

export const useGetOtcPrice = (args: UserGetOtcPriceParams) => {
  return useQuery(getOtcPriceOptions(args))
}

export const useGetOtcHistory = (params: UserOtcGetHistoryParams) => {
  return useInfiniteQuery(getOtcHistoryInfiniteOptions(params))
}

export const useOtcPlaceConvertOrder = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserOTCPlaceConvertOrderInput) =>
      userOtcService.otcPlaceConvertOrder(data),
    mutationKey: [EUserOtcQueryKey.OTC_PLACE_CONVERT_ORDER],
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserOtcQueryKey.GET_OTC_PRICE],
        })
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}

export const useOtcFinalizeConvertOrder = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserOTCFinalizeConvertOrderInput) =>
      userOtcService.otcFinalizeConvertOrder(data),
    mutationKey: [EUserOtcQueryKey.OTC_FINALIZE_CONVERT_ORDER],
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserOtcQueryKey.GET_OTC_PRICE],
        })
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}
